import Search from '@mui/icons-material/Search';
import { useGlobalContext } from "../../Context/GlobalContext";
import { useState, useCallback, useEffect } from "react";
import { MdRemoveCircle } from "react-icons/md";
import { LoaderCircle } from "lucide-react";

export default function SearchStaff({
  members,
  setMembers,
  names = [],
  title = "Members",
  noTitle = false,
  multiSelect = true, // New prop to control single/multiple selection
  onSelect = null, // Optional callback for single select mode
}) {
  const { searchedStaff, setSearchedStaff, searchStaff } = useGlobalContext();
  const [inputValue, setInputValue] = useState("");
  const [selectedStaff, setSelectedStaff] = useState(names);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setSearchedStaff(selectedStaff);
  }, [selectedStaff, setSearchedStaff]);

  const handleInputChange = useCallback(
    (e) => {
      const value = e.target.value.trim();
      setInputValue(value);

      if (value === "") {
        setSearchedStaff(null);
      } else {
        searchStaff(value, setIsLoading);
      }
    },
    [searchStaff, setSearchedStaff]
  );

  const handleAddOrRemoveMember = (staff) => {
    const staffId = staff?.staffDetails?.id || staff?.id;

    if (multiSelect) {
      if (members.includes(staffId)) {
        // Remove staff
        setMembers(members.filter((m) => m !== staffId));
        setSelectedStaff(selectedStaff.filter((s) => s !== staff));
      } else {
        // Add staff
        setMembers([...members, staffId]);
        setSelectedStaff([...selectedStaff, staff]);
      }
    } else {
      // Single select mode
      setMembers([staffId]);
      setSelectedStaff([staff]);
      setInputValue(""); // Clear input after selection
      setSearchedStaff(null); // Close dropdown
      if (onSelect) {
        onSelect(staff); // Trigger callback if provided
      }
    }

    // Trigger search again after selection in multi-select mode
    if (inputValue && multiSelect) {
      searchStaff(inputValue, setIsLoading);
    }
  };

  const handleRemoveStaff = (staff) => {
    const staffId = staff?.staffDetails?.id || staff?.id;
    setMembers(members.filter((m) => m !== staffId));
    setSelectedStaff(selectedStaff.filter((s) => s !== staff));
  };

  return (
    <div className="relative space-y-2 w-full billing-type-project">
      <div className="space-y-1">
        {!noTitle && (
          <label className="font-medium text-gray-700 text-sm">{title}</label>
        )}
        <div className="flex flex-wrap gap-2">
          {selectedStaff.map((staff) => (
            <div
              key={staff?.staffDetails?.id || staff?.id}
              className="flex items-center gap-1 bg-green-500 px-3 py-1 rounded-full text-[12px] text-white"
            >
              {staff?.name || staff?.User?.name}
              <MdRemoveCircle
                className="text-gray-600 cursor-pointer size-4"
                onClick={() => handleRemoveStaff(staff)}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="relative">
        <input
          className="border-[#DBDCDE] px-2 border rounded-md w-[100%] h-[35px]"
          type="text"
          name="members"
          placeholder={`Search ${multiSelect ? "Staff" : "Staff Member"}`}
          value={inputValue}
          onChange={handleInputChange}
        />
        {searchedStaff !== null && (
          <div className="top-[35px] left-0 z-20 absolute bg-gray-100 border rounded-md w-full max-h-36 overflow-y-scroll">
            {!searchedStaff && searchedStaff.length === 0 && (
              <p className="p-2 text-center">No staff found</p>
            )}
            {searchedStaff.length > 0 && (
              <ul>
                {searchedStaff.map((staff) => {
                  const staffId = staff?.staffDetails?.id || staff?.id;
                  // In single select mode, don't filter out selected staff
                  if (multiSelect && members.includes(staffId)) return null;

                  return (
                    <li
                      key={staffId}
                      className="hover:bg-gray-200 p-2 cursor-pointer"
                      onClick={() => handleAddOrRemoveMember(staff)}
                    >
                      {staff.name || staff.User.name}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
        {isLoading ? (
          <LoaderCircle className="top-[5px] right-[10px] absolute animate-spin" />
        ) : (
          <Search className="top-[5px] right-[10px] absolute" />
        )}
      </div>
    </div>
  );
}
